import React from 'react';
import _ from 'lodash';
import { useMutation } from 'react-fetching-library';
import { Card, CardHeader, CardContent, Modal, IconButton, Button, Stack, LinearProgress } from '@mui/material';
import { GridToolbar, plPL } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import { matchBankStatementsWithOrdersAction, matchBankStatementsWithPaymentsAction } from '../../../services/request';

import { SnackbarAlert } from '../../../components-core/SnackbarAlert';

import EditTransaction from './EditTransaction';
import SplitTransaction from './SplitTransaction';
import MatchInformation from './MatchInformation';
import MatchOrder from './MatchOrder';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const columns = [
  { field: 'amount', headerName: 'Kwota przelewu', width: 100, valueGetter: params => params.row.amount.amount },
  { field: 'entry_date', headerName: 'Data', width: 100 },
  { field: 'contractor', headerName: 'Kontrahent', width: 150 },
  { field: 'title', headerName: 'Tytuł płatności', width: 250 },
  { field: 'order_identifier', headerName: 'Dopasowane zamówienie', width: 190 },
  { field: 'payment_name', headerName: 'Nazwa dopasowanej płatności', width: 230 },
  { field: 'payment_value', headerName: 'Kwota płatności', width: 130 },
  { field: 'participant', headerName: 'Uczestnik', width: 120 },
  { field: 'guardian', headerName: 'Opiekun', width: 120 },
  { field: 'type', headerName: ' ', width: 120 }
];

const Transactions = ({ open, handleClose, transactions, setTransactions }) => {
  const { mutate, loading } = useMutation(matchBankStatementsWithOrdersAction);
  const { mutate: mutateConfirm, loading: loadingConfrim } = useMutation(matchBankStatementsWithPaymentsAction);
  const [selectionModel, setSelectionModel] = React.useState([]);
  const [openSecondModal, setOpenSecondModal] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [secondModalType, setSecondModalType] = React.useState(0);
  const [transaction, setTransaction] = React.useState({});
  const [matchInfo, setMatchInfo] = React.useState({});
  const [allMatches, setAllMatches] = React.useState([]);
  const [message, setMessage] = React.useState('');

  const handleOpenSecondModal = () => {
    const selectedTransaction = transactions.find(transaction => transaction.id === selectionModel[0]);
    setTransaction(selectedTransaction);
    setOpenSecondModal(true);
  };

  const handleMatchSelected = async () => {
    if (selectionModel.length > 0) {
      const selectedTransaction = transactions.find(transaction => transaction.id === selectionModel[0]);
      const { error: mutateError, payload } = await mutate({ 'transactions': [selectedTransaction] });
      if (!mutateError) {
        setTransactions(prev => prev.map(t => t.id === payload[0].id ? payload[0] : t));
      }
    } else {
      setOpenSnackbar(true);
    }
  };

  const handleMatchAll = async () => {
    const { error: mutateError, payload } = await mutate({ transactions });
    if (!mutateError) {
      setTransactions(payload);
    }

  };

  const handleCloseSecondModal = () => {
    setOpenSecondModal(false);
    setSecondModalType(0);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
    setMessage('');
  };

  const handleDeleteTransaction = () => {
    if (selectionModel.length > 0) {
      setTransactions(prev => prev.filter(transaction => transaction.id !== selectionModel[0]));
    } else {
      setOpenSnackbar(true);
    }
  };

  const handleEditTransaction = () => {
    if (selectionModel.length > 0) {
      setSecondModalType(1);
      handleOpenSecondModal();
    } else {
      setOpenSnackbar(true);
    }
  };

  const handleSplitTransaction = () => {
    if (selectionModel.length > 0) {
      setSecondModalType(2);
      handleOpenSecondModal();
    } else {
      setOpenSnackbar(true);
    }
  };

  const handleConfirmMatchedPayments = async () => {
    const { error: mutateError, payload } = await mutateConfirm({ transactions });
    if (!mutateError) {
      setTransactions(prev => prev.filter(t => !payload.id_list.includes(t.payment_id)));
    }
  };

  const handleRemoveMatchedPayment = () => {
    if (selectionModel.length > 0) {
      const selectedTransaction = transactions.find(transaction => transaction.id === selectionModel[0]);
      Reflect.deleteProperty(selectedTransaction, 'order_id');
      Reflect.deleteProperty(selectedTransaction, 'order_identifier');
      Reflect.deleteProperty(selectedTransaction, 'payment_id');
      Reflect.deleteProperty(selectedTransaction, 'payment_name');
      Reflect.deleteProperty(selectedTransaction, 'payment_value');
      Reflect.deleteProperty(selectedTransaction, 'participant');
      Reflect.deleteProperty(selectedTransaction, 'guardian');
      Reflect.deleteProperty(selectedTransaction, 'type');
      setTransactions(prev => prev.map(t => t.id === selectedTransaction.id ? selectedTransaction : t));
    } else {
      setOpenSnackbar(true);
    }
  };


  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          setSelectionModel([]);
          handleClose();
        }}
      >
        <Card sx={{ ...style, width: '100vw', height: '100vh' }}>
          <CardHeader
            title="Transakcje"
            action={
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectionModel([]);
                  handleClose();
                }}>
                <CloseIcon />
              </IconButton>
            }
          />
          <CardContent sx={{ height: '80%' }}>
            <DataGridPro
              rows={transactions}
              columns={columns}
              pagination
              pageSize={100}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              checkboxSelection={false}
              components={{ Toolbar: GridToolbar }}
              localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
              onSelectionModelChange={newSelection => {
                setSelectionModel(newSelection);
              }}
              selectionModel={selectionModel}
            />
            <Stack sx={{ height: '20%' }} direction="row" alignItems="center" spacing={3}>
              {loading || loadingConfrim
                ? <LinearProgress sx={{ width: '100%' }}/>
                : <>
                  <Button variant="outlined" onClick={handleMatchSelected}>Dopasuj dla zaznaczonego</Button>
                  <Button variant="outlined" onClick={handleMatchAll}>Dopasuj wszystkie</Button>
                  <Button variant="outlined" color="secondary" startIcon={<EditIcon />} onClick={handleEditTransaction}>Edytuj pozycje</Button>
                  <Button variant="outlined" color="secondary" onClick={handleSplitTransaction}>Rozdziel pozycje</Button>
                  <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteTransaction}>Usuń pozycje</Button>
                  <Button variant="outlined" color="error" onClick={handleRemoveMatchedPayment}>Usuń dopasowanie</Button>
                  <Button variant="contained" color="primary" onClick={handleConfirmMatchedPayments}>Zatwierdź dopasaowania</Button>
                </>
              }


            </Stack>
            <SnackbarAlert open={openSnackbar} severity="info" message={message !== '' ? message : 'Wybierz transakcje'} onClose={handleCloseSnackbar} />
          </CardContent>
        </Card>
      </Modal>
      {secondModalType === 1
        ? <EditTransaction open={openSecondModal} handleClose={handleCloseSecondModal} transaction={transaction} setTransactions={setTransactions} />
        : secondModalType === 2
          ? <SplitTransaction open={openSecondModal} handleClose={handleCloseSecondModal} transaction={transaction} setTransactions={setTransactions} />
          : secondModalType === 3
            ? <MatchInformation open={openSecondModal} handleClose={handleCloseSecondModal} matchInfo={matchInfo} />
            : secondModalType === 4
              ? <MatchOrder open={openSecondModal} handleClose={handleCloseSecondModal} orders={allMatches} transaction={transaction} setTransactions={setTransactions} />
              : null}
    </>
  );
};

export default Transactions;
